import React from 'react';
import { motion } from 'framer-motion';
import { Phone, ChevronDown } from 'lucide-react';

const HERO_IMG = 'https://images.pexels.com/photos/37252662/pexels-photo-37252662.jpeg?auto=compress&cs=tinysrgb&w=1920&q=85';

const words = ['Ihr', 'Zuhause', 'verdient', 'das', 'Beste.', 'Seit', '1988.'];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.4 } },
};
const wordVariants = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

const Hero = () => {
  const scrollToContact = () => {
    document.querySelector('#kontakt')?.scrollIntoView({ behavior: 'smooth' });
  };
  const scrollDown = () => {
    document.querySelector('#referenzen')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Ken-Burns background */}
      <div
        className="absolute inset-0 ken-burns-bg bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_IMG})` }}
        data-testid="hero-bg"
      />

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/72 via-black/38 to-black/18" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/25 to-transparent" />

      {/* Content */}
      <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto pt-20">
        {/* Top badge */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="inline-flex items-center gap-2.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-2 mb-10"
          data-testid="hero-badge-top"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#C4944A] flex-shrink-0" />
          <span className="text-white/88 text-[11px] tracking-[0.2em] uppercase font-outfit">
            Berliner Familienunternehmen · Meisterbetrieb seit 1988
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          className="font-cormorant text-5xl sm:text-6xl lg:text-7xl font-medium text-white leading-[1.1] mb-7"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          data-testid="hero-headline"
        >
          {words.map((word, i) => (
            <motion.span key={i} variants={wordVariants} className="inline-block mr-[0.25em]">
              {word}
            </motion.span>
          ))}
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.0 }}
          className="font-outfit text-base sm:text-lg text-white/82 mb-11 max-w-2xl mx-auto leading-relaxed"
          data-testid="hero-subheadline"
        >
          Gardinen · Polsterei · Sonnenschutz · Raumausstattung aus einer Hand in Berlin-Heiligensee
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.2 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          data-testid="hero-cta-group"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={scrollToContact}
            className="cta-pulse-btn bg-[#8B7355] text-white px-9 py-4 font-outfit text-xs tracking-[0.18em] uppercase hover:bg-[#6B5440] transition-colors duration-200 min-w-[220px]"
          >
            Jetzt beraten lassen
          </button>
          <a
            href="tel:+493043115080"
            data-testid="hero-cta-phone"
            className="flex items-center gap-2.5 border border-white/55 text-white px-9 py-4 font-outfit text-xs tracking-[0.18em] uppercase hover:bg-white/12 transition-colors duration-200 min-w-[220px] justify-center"
          >
            <Phone size={13} />
            030 / 4 31 15 80
          </a>
        </motion.div>
      </div>

      {/* Floating badge bottom right */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 1.6 }}
        className="absolute bottom-10 right-6 sm:right-10 bg-white/10 backdrop-blur-md border border-white/18 px-5 py-4"
        data-testid="hero-floating-badge"
      >
        <div className="text-white/88 text-[11px] font-outfit space-y-1.5">
          {['Meisterbetrieb', '35 Jahre Berlin', '10.200+ Kunden'].map((item) => (
            <div key={item} className="flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-[#C4944A]" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.button
        onClick={scrollDown}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.0, duration: 0.5 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/55 flex flex-col items-center gap-1.5 hover:text-white/80 transition-colors"
        data-testid="hero-scroll-down"
      >
        <span className="text-[10px] tracking-[0.2em] uppercase font-outfit">Entdecken</span>
        <ChevronDown size={15} className="animate-bounce" />
      </motion.button>
    </section>
  );
};

export default Hero;
